import { argentinaNationalId } from './countries/argentina';
import { brazilNationalId } from './countries/brazil';
import { chileNationalId } from './countries/chile';
import { fallbackNationalId } from './countries/fallback';
import type {
  JurisdictionIdentifier,
  LegalIdentifier,
  NationalIdRules,
} from './types';

export type {
  JurisdictionIdentifier,
  LegalIdentifier,
  NationalIdRules,
} from './types';
export { argentinaNationalId } from './countries/argentina';
export { brazilNationalId } from './countries/brazil';
export { chileNationalId } from './countries/chile';
export { fallbackNationalId } from './countries/fallback';

/** ISO alpha-2 → rules. Add a country by registering here. */
const BY_ISO: Record<string, NationalIdRules> = {
  CL: chileNationalId,
  AR: argentinaNationalId,
  BR: brazilNationalId,
};

const REQUIRED_ATTENDANCE_ID_BY_ISO: Record<string, NationalIdRules> = {
  CL: chileNationalId,
};

/** Common display / org names → ISO. Extend when adding a country. */
const NAME_TO_ISO: Record<string, string> = {
  chile: 'CL',
  argentina: 'AR',
  brazil: 'BR',
  brasil: 'BR',
  default: 'XX',
};

export type OrganizationCountrySource = {
  country?: string | null;
  address?: { country?: string | null } | null;
  /** Redux keeps the organization document under `data`. */
  data?: OrganizationCountrySource | null;
} | null;

/**
 * Resolves the organization country from either the organization document or
 * the Redux `organizations.organization` node.
 *
 * The configured `country` wins: it is the field the organization form edits.
 * `address.country` is only a fallback for organizations that never set it,
 * since it comes from geocoding the postal address and can name a different
 * country than the one the organization operates under.
 */
export const getOrganizationCountry = (
  organization?: OrganizationCountrySource,
): string | null =>
  organization?.country ||
  organization?.address?.country ||
  (organization?.data ? getOrganizationCountry(organization.data) : null) ||
  null;

/**
 * Resolves a country string (ISO code, Nominatim name, or org country name) to ISO alpha-2.
 */
export const resolveCountryCode = (country?: string | null): string | null => {
  if (!country || typeof country !== 'string') {
    return null;
  }
  const trimmed = country.trim();
  if (!trimmed) {
    return null;
  }
  const upper = trimmed.toUpperCase();
  if (upper.length === 2 && /^[A-Z]{2}$/.test(upper)) {
    return upper;
  }
  const mapped = NAME_TO_ISO[trimmed.toLowerCase()];
  return mapped || null;
};

/**
 * Returns national-ID rules for the given country (address or org).
 * Unknown / empty country → neutral fallback.
 */
export const getNationalIdRules = (country?: string | null): NationalIdRules => {
  const code = resolveCountryCode(country);
  if (!code || code === 'XX') {
    return fallbackNationalId;
  }
  return BY_ISO[code] || fallbackNationalId;
};

export const formatNationalId = (country: string | null | undefined, value: string): string =>
  getNationalIdRules(country).format(value || '');

export const normalizeNationalId = (country: string | null | undefined, value: string): string =>
  getNationalIdRules(country).normalize(value || '');

export const validateNationalId = (country: string | null | undefined, value: string): boolean =>
  getNationalIdRules(country).validate(value || '');

export const getRequiredAttendanceIdRules = (
  organizationCountry?: string | null,
): NationalIdRules | null => {
  const code = resolveCountryCode(organizationCountry);
  return code ? REQUIRED_ATTENDANCE_ID_BY_ISO[code] || null : null;
};

/** True when personal country is known and differs from the organization country. */
export const isForeignPersonalCountry = (
  organizationCountry?: string | null,
  personalCountry?: string | null,
): boolean => {
  const orgCode = resolveCountryCode(organizationCountry);
  const personalCode = resolveCountryCode(personalCountry);
  return !!orgCode && !!personalCode && personalCode !== orgCode;
};

/**
 * Countries whose `validate()` is strong enough for cross-jurisdiction auto-detect
 * (checksum / high entropy). Length-only rules (e.g. Argentine DNI) must not be
 * guessed from `idCard` alone — require `personalCountry` instead.
 */
const FOREIGN_NATIONAL_ID_AUTO_DETECT_CODES = new Set(['BR']);

/**
 * Detects when `personalIdCard` is a valid national ID for a registered country
 * other than the organization jurisdiction (e.g. Brazilian CPF in a Chilean org).
 * Only checksummed formats are auto-detected; see `FOREIGN_NATIONAL_ID_AUTO_DETECT_CODES`.
 */
export const detectForeignNationalIdCountry = (
  organizationCountry: string | null | undefined,
  personalIdCard: string,
): string | null => {
  const orgCode = resolveCountryCode(organizationCountry);
  const value = personalIdCard || '';
  if (!value) {
    return null;
  }
  for (const [code, rules] of Object.entries(BY_ISO)) {
    if (code === orgCode || !FOREIGN_NATIONAL_ID_AUTO_DETECT_CODES.has(code)) {
      continue;
    }
    if (rules.validate(value)) {
      return code;
    }
  }
  return null;
};

/**
 * Rules used when prompting for / validating a jurisdiction overlay.
 * Local workers keep the strict attendance document (RUT); foreign workers
 * get a loose generic identifier instead of a forced local checksum format.
 */
export const getAttendanceJurisdictionIdRules = (
  organizationCountry?: string | null,
  personalCountry?: string | null,
): NationalIdRules | null => {
  const required = getRequiredAttendanceIdRules(organizationCountry);
  if (!required) {
    return null;
  }
  if (isForeignPersonalCountry(organizationCountry, personalCountry)) {
    return {
      ...fallbackNationalId,
      countryCode: required.countryCode,
      // Loose org-scoped id for foreign workers — not a checksummed local RUT.
      documentKind: 'ID',
    };
  }
  return required;
};

const hasValidForeignPersonalId = (
  organizationCountry: string | null | undefined,
  personalIdCard: string,
  personalCountry?: string | null,
): boolean => {
  if (isForeignPersonalCountry(organizationCountry, personalCountry)) {
    return validateNationalId(personalCountry, personalIdCard || '');
  }
  return !!detectForeignNationalIdCountry(organizationCountry, personalIdCard || '');
};

export const isValidJurisdictionIdentifier = (
  organizationCountry: string | null | undefined,
  identifier?: JurisdictionIdentifier | null,
  personalCountry?: string | null,
): boolean => {
  if (!identifier) {
    return false;
  }
  const promptRules = getAttendanceJurisdictionIdRules(organizationCountry, personalCountry);
  if (!promptRules) {
    return false;
  }
  const countryCode = identifier.countryCode?.trim().toUpperCase();
  const documentKind = identifier.documentKind?.trim().toUpperCase();
  if (countryCode !== promptRules.countryCode) {
    return false;
  }
  if (
    documentKind === promptRules.documentKind.toUpperCase() &&
    promptRules.validate(identifier.value || '')
  ) {
    return true;
  }
  // Legacy overlays stored as the strict local document (RUT) remain valid.
  const required = getRequiredAttendanceIdRules(organizationCountry);
  return (
    !!required &&
    documentKind === required.documentKind.toUpperCase() &&
    required.validate(identifier.value || '')
  );
};

/** Personal id already satisfies attendance (local rules or valid foreign document). */
const hasSufficientPersonalIdForAttendance = (
  organizationCountry: string | null | undefined,
  personalIdCard: string,
  personalCountry?: string | null,
): boolean => {
  const rules = getRequiredAttendanceIdRules(organizationCountry);
  if (!rules) {
    return true;
  }
  if (rules.validate(personalIdCard || '')) {
    return true;
  }
  return hasValidForeignPersonalId(organizationCountry, personalIdCard, personalCountry);
};

export const needsJurisdictionIdentifier = (
  organizationCountry: string | null | undefined,
  personalIdCard: string,
  identifier?: JurisdictionIdentifier | null,
  personalCountry?: string | null,
): boolean => {
  if (hasSufficientPersonalIdForAttendance(organizationCountry, personalIdCard, personalCountry)) {
    return false;
  }
  return !isValidJurisdictionIdentifier(organizationCountry, identifier, personalCountry);
};

export const buildJurisdictionIdentifier = (
  organizationCountry: string | null | undefined,
  value: string,
  personalCountry?: string | null,
): JurisdictionIdentifier | null => {
  const rules = getAttendanceJurisdictionIdRules(organizationCountry, personalCountry);
  if (!rules || !rules.validate(value || '')) {
    return null;
  }
  return {
    countryCode: rules.countryCode,
    documentKind: rules.documentKind,
    value: rules.normalize(value),
  };
};

/**
 * Resolves the immutable legal identifier copied to attendance/work-journey
 * snapshots.
 *
 * Priority when the organization requires a local attendance id (e.g. Chile):
 * 1. Personal id that already satisfies the local rules (RUT)
 * 2. Explicit organization-scoped jurisdiction overlay, when present and valid
 * 3. Valid foreign personal id (e.g. Brazilian CPF) as a fallback when no overlay
 */
export const resolveLegalIdentifier = ({
  organizationCountry,
  personalCountry,
  personalIdCard,
  jurisdictionIdentifier,
}: {
  organizationCountry?: string | null;
  personalCountry?: string | null;
  personalIdCard: string;
  jurisdictionIdentifier?: JurisdictionIdentifier | null;
}): LegalIdentifier | null => {
  const requiredRules = getRequiredAttendanceIdRules(organizationCountry);
  if (requiredRules) {
    if (requiredRules.validate(personalIdCard || '')) {
      return {
        countryCode: requiredRules.countryCode,
        documentKind: requiredRules.documentKind,
        value: requiredRules.normalize(personalIdCard),
        source: 'personal',
      };
    }
    // Prefer an explicit jurisdiction overlay over a foreign personal document so
    // Chilean attendance snapshots keep the RUT when one was collected.
    if (isValidJurisdictionIdentifier(organizationCountry, jurisdictionIdentifier, personalCountry)) {
      const promptRules = getAttendanceJurisdictionIdRules(organizationCountry, personalCountry);
      const documentKind = jurisdictionIdentifier!.documentKind;
      const normalizeWith =
        promptRules &&
        documentKind?.trim().toUpperCase() === promptRules.documentKind.toUpperCase()
          ? promptRules
          : requiredRules;
      return {
        countryCode: normalizeWith.countryCode,
        documentKind: documentKind || normalizeWith.documentKind,
        value: normalizeWith.normalize(jurisdictionIdentifier!.value),
        source: 'jurisdiction',
      };
    }
    const foreignCode =
      (isForeignPersonalCountry(organizationCountry, personalCountry) &&
      validateNationalId(personalCountry, personalIdCard || '')
        ? resolveCountryCode(personalCountry)
        : null) || detectForeignNationalIdCountry(organizationCountry, personalIdCard || '');
    if (foreignCode) {
      const foreignPersonalRules = getNationalIdRules(foreignCode);
      return {
        countryCode: foreignPersonalRules.countryCode,
        documentKind: foreignPersonalRules.documentKind,
        value: foreignPersonalRules.normalize(personalIdCard),
        source: 'personal',
      };
    }
    return null;
  }

  const personalRules = getNationalIdRules(personalCountry);
  const normalized = personalRules.normalize(personalIdCard || '');
  return normalized
    ? {
        countryCode: personalRules.countryCode,
        documentKind: personalRules.documentKind,
        value: normalized,
        source: 'personal',
      }
    : null;
};

/**
 * Display string for attendance lists: prefer the resolved personal / jurisdiction
 * identifier, then fall back to formatting a raw personal idCard.
 */
export const formatAttendanceIdentifierDisplay = (
  organizationCountry: string | null | undefined,
  user: {
    idCard?: string | null;
    jurisdictionId?: JurisdictionIdentifier | null;
    address?: { country?: string | null } | null;
  },
): string => {
  const resolved = resolveLegalIdentifier({
    organizationCountry,
    personalCountry: user.address?.country,
    personalIdCard: user.idCard || '',
    jurisdictionIdentifier: user.jurisdictionId,
  });
  if (resolved) {
    return formatNationalId(resolved.countryCode, resolved.value) || resolved.value;
  }
  return formatNationalId(organizationCountry, user.idCard || '') || '';
};

export const nationalIdSearchToken = (
  country: string | null | undefined,
  value: string,
): string => getNationalIdRules(country).searchToken(value || '');

/** Registered ISO codes (excluding fallback). */
export const supportedNationalIdCountryCodes = (): string[] => Object.keys(BY_ISO);
